import {
  Injectable,
  ApplicationRef,
  Injector,
  ComponentRef,
  ViewContainerRef,
} from '@angular/core';
import { of, Observable, Subject, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  NotificationMessageData,
  NotificationSidebarComponent,
} from '@app/framework/notification-sidebar/notification-sidebar.component';
import { ScaffoldSidebarService } from '@app/framework/sidebar/scaffold-sidebar.service';
import { ScaffoldToastComponent } from '@app/framework/notifcation/scaffold-toast/scaffold-toast.component';

export class NotificationLink {
  route?: string;
  label?: string;
}

export interface NotificationStatus {
  activeNotificationCount: number;
}

export class NotificationSettings {
  /** Color of dialog (primary, info, warning, danger, default)*/
  status?;
  /** Position of message */
  position?;
  /** Display an icon in the window*/
  hasIcon? = false;
  /** Icon configuration {pack:"", icon:"" }*/
  icon?: any;
  /** Alternate to status to match prime ng severity */
  severity?;
}

export class NotificationItem {
  constructor(settings) {
    Object.keys(settings).forEach(k => {
      this[k] = settings[k];
    });
  }
  /**Message to display*/
  message: string;
  /**Message item in database to reference*/
  reference?: any;
  /**Status of the message to conbtrol background color*/
  status = 'basic';
  /**Dismissal status, used for hiding message*/
  dismiss? = false;
  /** Router link*/
  link?: NotificationLink;
  /**Label for close button*/
  close?;
  /**Close event*/
  onClose?: Subject<any>;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private _barNotifications: NotificationItem[] = [];

  sidebarRef: any;
  toastHolder: ComponentRef<ScaffoldToastComponent>;
  private user_notification = new BehaviorSubject({});
  public user_notification$ = this.user_notification.asObservable();

  constructor(
    private sidebar: ScaffoldSidebarService,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {
    //Temp disable storing notification until we have a beter handler to retreve them
    /*const userNotifications = JSON.parse(
      window.localStorage.getItem("user_notifications")
    );
    if (userNotifications) this.user_notification.next(userNotifications);*/
  }
  /**
   * Display an alert
   * @param message Message to be displayed
   * @param action Title of the notification
   * @param duration Durration in seconds
   * @param additional Configiration parameters onject { status: color of message(primary, info, warning, danger, generic) }
   */
  popNotification(
    message,
    action,
    duration: number = 5,
    additional?: NotificationSettings
  ) {
    this.createTostHolder();
    let icon;
    if (additional?.icon) {
      if (additional?.icon?.pack && additional?.icon?.icon) {
        icon = additional?.icon.pack + ' fa-' + additional?.icon.icon;
      } else if (typeof additional?.icon === 'string') {
        icon = additional?.icon;
      }
    }
    const settings: any = {
      life: duration * 1000,
      severity: additional?.status || additional?.severity || 'info',
      detail: action,
      summary:
        message ||
        (additional?.status
          ? additional?.status.charAt(0).toUpperCase() +
            additional?.status?.slice(1)
          : 'Info'),
      icon,
    };

    this.toastHolder.instance.open(settings);
  }

  /**
   * Create a bar notification
   * @param message message to display
   * @param status status of the message
   */
  createBarNotification(settings: NotificationItem | any) {
    let notification: NotificationItem;
    if (settings instanceof NotificationItem) {
      notification = settings;
    } else {
      notification = new NotificationItem(settings);
    }
    const onClose: Subject<any> = new Subject();
    notification.onClose = onClose;
    this._barNotifications.push(notification);
    return notification.onClose.asObservable();
  }

  /**
   * Dissmiss a bar notification
   * @param bar notification to dismiss
   */
  dismissBar(bar: NotificationItem) {
    if (bar.onClose) {
      bar.onClose.next(null);
      bar.onClose.complete();
    }
    bar.dismiss = true;
  }

  /**Subscribe to the list of bar notifications */
  barNotifications(): Observable<NotificationItem[]> {
    return of(this._barNotifications.filter(n => !n.dismiss));
  }

  addUserNotification(message: NotificationMessageData, id: string) {
    const { value } = this.user_notification;

    value[id] = { ...message, id, date: new Date() };
    this.user_notification.next(value);
    //  window.localStorage.setItem("user_notifications", this.notificationToString(value));
    if (message.shouldForceOpen && !this.sidebarRef) this.showSidebar();
  }

  /**Displays the list of user notifications */
  showSidebar() {
    let config = {
      context: {
        messages: this.user_notification$.pipe(
          map(notifications => {
            let result = Object.keys(notifications).reduce(
              (acc, notification, i, arr) => {
                let message: NotificationMessageData =
                  notifications[notification];

                if (message?.isOld) {
                  //if old check to see if in new and prevent adding if already there with that notification id
                  if (!acc.newMessages[notification]) {
                    acc.oldMessages[notification] = message;
                  }
                } else {
                  //if new check to remove in old array before adding
                  if (acc.oldMessages[notification])
                    delete acc.oldMessages[notification];
                  acc.newMessages[notification] = message;
                }

                return acc;
              },
              { oldMessages: {}, newMessages: {} }
            ) || { oldMessages: {}, newMessages: {} };
            return result;
          })
        ),
      },
    } as any;
    this.sidebarRef = this.sidebar.open(NotificationSidebarComponent, config);

    const closeSingleNotification =
      this.sidebarRef?.componentRef?.instance?.closeSingleNotification?.subscribe(
        notification => {
          const { value } = this.user_notification;
          if (notification?.id && !notification.preventRemoval)
            delete value[notification.id];
          this.user_notification.next(value);
          window.localStorage.setItem(
            'user_notifications',
            this.notificationToString(value)
          );
        }
      );

    const closeAllNotifications =
      this.sidebarRef?.componentRef?.instance?.closeAllNotifications?.subscribe(
        () => {
          const { value } = this.user_notification;

          const updatedValue = this.closeAllNotifications({ ...value });
          this.user_notification.next(updatedValue);
          window.localStorage.setItem(
            'user_notifications',
            this.notificationToString(updatedValue)
          );
        }
      );

    this.sidebarRef.onClose.subscribe(res => {
      this.sidebarRef = null;
      this.markNotificationsAsOld();
      closeSingleNotification.unsubscribe();
      closeAllNotifications.unsubscribe();
    });
  }

  closeAllNotifications(notifications) {
    Object.keys(notifications).forEach(key => {
      // if (!notifications[key]?.preventRemoval) {
      delete notifications[key];
      // }
    });

    return notifications;
  }

  markNotificationsAsOld() {
    const { value } = this.user_notification;
    Object.keys(value).forEach(key => (value[key].isOld = true));
    this.user_notification.next(value);
    //  window.localStorage.setItem("user_notifications", this.notificationToString(value));
  }
  // NotificationStatus
  getNotificationStatus(): Observable<NotificationStatus> {
    return this.user_notification$.pipe(
      map(notifications => {
        return Object.keys(notifications).reduce(
          (acc, notification) => {
            return !notifications[notification].isOld
              ? { activeNotificationCount: acc.activeNotificationCount + 1 }
              : acc;
          },
          {
            activeNotificationCount: 0,
          }
        );
      })
    );
  }
  /**
   *  Convert a notification to a string
   * @param notification
   */
  notificationToString(notification): string {
    let seen = [];
    return JSON.stringify(notification, function (ind, val) {
      if (val && typeof val === 'object') {
        if (seen.indexOf(val) >= 0) {
          return 'Cyclic Object';
        }
        seen.push(val);
      }
      return val;
    });
  }

  createTostHolder() {
    if (this.toastHolder) {
      return this.toastHolder;
    }
    const rootViewContainerRef =
      this.appRef.components[0].injector.get(ViewContainerRef);
    this.toastHolder = rootViewContainerRef.createComponent(
      ScaffoldToastComponent,
      { injector: this.injector }
    );
    // make sure component is properly initialized
    this.toastHolder.changeDetectorRef.detectChanges();
    return this.toastHolder;
  }
}
